import { useCallback } from 'react';
import { useScreenNavigation } from './useScreenNavigation';
import { useDisplayModeContext } from './useDisplayModeContext';
import { sessionStateManager } from '../utils/SessionStateManager';
import { handleVerificationComplete } from '../utils/redirectHandler';
import { attemptCloseTab, isNewTabMode } from '../utils/newTabHandler';
import { logger } from '../utils/logger';
import { useLanguage } from '../i18n';

const continueLogger = logger.clone({ labels: { component: 'ContinueRedirect' }});

interface ContinueRedirectOptions {
  isEmblemVerification: boolean;
  sessionId?: string;
  returnUrl?: string | null;
}

export function useContinueRedirect({
  isEmblemVerification,
  sessionId,
  returnUrl,
}: ContinueRedirectOptions) {
  const { navigateToScreen } = useScreenNavigation();
  const displayModeContext = useDisplayModeContext();
  const { t } = useLanguage();
  const isInNewTabMode = isNewTabMode();

  const handleContinue = useCallback(async (options?: { forceRedirect?: boolean }) => {
    const forceRedirect = options?.forceRedirect === true;

    if (isInNewTabMode && !forceRedirect) {
      const closed = await attemptCloseTab();
      if (closed) {
        return;
      }
      continueLogger.info(
        'Window.close() was blocked; falling back to returnUrl redirect'
      );
      // Fall through to handleVerificationComplete so the user is not stranded
      // when the browser refuses to close a cross-origin-navigated popup
      // (e.g. Firefox: "Scripts may only close windows that were opened by a script").
    }

    const sessionState = sessionStateManager.getState();
    const stateSessionId = sessionState?.sessionId;
    const stateReturnUrl = sessionState?.config?.returnUrl;
    const stateCancelUrl = sessionState?.config?.cancelUrl;
    const stateExternalUserId = sessionState?.config?.externalUserId;
    const effectiveExternalUserId = stateExternalUserId;

    const effectiveSessionId = stateSessionId || sessionId;
    const effectiveReturnUrl =
      stateReturnUrl || returnUrl || displayModeContext.returnUrl;
    const effectiveCancelUrl = stateCancelUrl;

    if (!effectiveReturnUrl) {
      continueLogger.error('Critical error: Success reached without returnUrl', {
        labels: {
          contextReturnUrl: `${returnUrl ?? displayModeContext.returnUrl}`,
          stateReturnUrl: `${stateReturnUrl}`,
          isEmblemVerification: `${isEmblemVerification}`,
        }
      });

      navigateToScreen('failure', {
        verificationResult: {
          type: 'verification_result',
          success: false,
          liveness_result: false,
          message: t('success.errors.missingReturnUrl'),
          requires_id_verification: false,
        }
      });
      return;
    }

    if (effectiveSessionId && effectiveReturnUrl) {
      handleVerificationComplete({
        sessionId: effectiveSessionId,
        status: 'verified',
        returnUrl: effectiveReturnUrl,
        cancelUrl: effectiveCancelUrl || undefined,
        externalUserId: effectiveExternalUserId,
      });
    } else {
      window.location.href = effectiveReturnUrl;
    }
  }, [
    displayModeContext.returnUrl,
    isEmblemVerification,
    isInNewTabMode,
    navigateToScreen,
    returnUrl,
    sessionId,
    t,
  ]);

  return { handleContinue, isInNewTabMode };
}
